import Close from '@mui/icons-material/Close';
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useLastVisible } from '~/shared/hooks/useLastVisible';
import {
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogPaper,
  StyledSubmitButton,
} from '~/modules/matters/modals/shared';
import { useToast } from '~/shared/contexts/ToastContext';
import { retainerCardTitle } from '~/modules/matters/detail/retainersTabUtils';
import { RetainerStatus, type RetainerFieldsFragment } from '~/gql/graphql';
import { useRetainerMutations } from '~/modules/matters/useRetainerMutations';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
  retainer: RetainerFieldsFragment | null;
  onClosed?: () => void;
}

const CloseRetainerModal = ({
  isOpen,
  onClose,
  matterId,
  retainer: retainerProp,
  onClosed,
}: Props) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { showToast } = useToast();
  const { closeRetainer, isClosingRetainer } = useRetainerMutations();
  const [closedMessage, setClosedMessage] = useState('');

  const retainer = useLastVisible(retainerProp, isOpen);

  const handleClose = () => {
    if (isClosingRetainer) {
      return;
    }
    setClosedMessage('');
    onClose();
  };

  const handleConfirm = async () => {
    if (!retainer) {
      return;
    }
    const closingPending = retainer.status === RetainerStatus.Pending;
    try {
      await closeRetainer(
        matterId,
        retainer.id,
        closedMessage.trim() || undefined,
      );
      if (closingPending) {
        showToast('Request withdrawn.');
      } else {
        showToast('Retainer closed.');
      }
      onClosed?.();
      setClosedMessage('');
      onClose();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'close_retainer' },
        extra: { matterId, retainerId: retainer?.id },
      });
      const message =
        error instanceof Error
          ? error.message
          : 'Something went wrong. Please try again.';
      showToast(message, 'error');
    }
  };

  const isPending = retainer?.status === RetainerStatus.Pending;
  const title = isPending ? 'Withdraw retainer request' : 'Close retainer';
  const submitLabel = isPending ? 'Withdraw request' : 'Close retainer';
  const submitProgressLabel = isPending ? 'Withdrawing…' : 'Closing…';

  let subtitle: ReactNode;
  if (!retainer) {
    subtitle = 'No retainer selected.';
  } else if (isPending) {
    subtitle = (
      <>
        Withdraw this pending request? It will move to retainer history. You can
        optionally add a comment. Retainer:{' '}
        <RetainerNameInCopy>{retainerCardTitle(retainer)}</RetainerNameInCopy>
      </>
    );
  } else {
    subtitle = (
      <>
        Close this retainer? It will move to retainer history. You can
        optionally add a comment. Retainer:{' '}
        <RetainerNameInCopy>{retainerCardTitle(retainer)}</RetainerNameInCopy>
      </>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      fullScreen={fullScreen}
      PaperComponent={StyledDialogPaper}
    >
      <ModalHeaderRoot>
        <CloseIconRow>
          <HeaderCloseButton
            aria-label="Close"
            onClick={handleClose}
            size="small"
            disabled={isClosingRetainer}
          >
            <HeaderCloseIcon aria-hidden />
          </HeaderCloseButton>
        </CloseIconRow>
        <HeaderTitleBlock>
          <ModalTitleText variant="h3">{title}</ModalTitleText>
          {subtitle !== null && subtitle !== undefined && subtitle !== '' && (
            <ModalDescription variant="body2" color="text.secondary">
              {subtitle}
            </ModalDescription>
          )}
        </HeaderTitleBlock>
      </ModalHeaderRoot>
      <StyledDialogContent>
        {retainer ? (
          <CommentFieldStack>
            <TextField
              placeholder="Comment (optional)"
              value={closedMessage}
              onChange={(event) => setClosedMessage(event.target.value)}
              multiline
              minRows={4}
              fullWidth
              variant="outlined"
              size="medium"
              hiddenLabel
              inputProps={{
                'aria-label': 'Comment (optional)',
              }}
            />
          </CommentFieldStack>
        ) : null}
      </StyledDialogContent>
      <CloseRetainerDialogActions>
        <CloseRetainerSubmitButton
          onClick={handleConfirm}
          variant="contained"
          disabled={!retainer || isClosingRetainer}
          aria-busy={isClosingRetainer}
          aria-label={
            isClosingRetainer ? `${submitLabel} in progress` : submitLabel
          }
          startIcon={
            isClosingRetainer ? (
              <CircularProgress size={16} color="inherit" aria-hidden />
            ) : undefined
          }
        >
          {isClosingRetainer ? submitProgressLabel : submitLabel}
        </CloseRetainerSubmitButton>
      </CloseRetainerDialogActions>
    </Dialog>
  );
};

const ModalHeaderRoot = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
}));

const CloseIconRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(-0.5),
  marginRight: theme.spacing(-0.5),
}));

const HeaderCloseButton = styled(IconButton)({
  flexShrink: 0,
});

const HeaderCloseIcon = styled(Close)({
  fontSize: 20,
});

const HeaderTitleBlock = styled('div')({
  textAlign: 'center',
});

const ModalTitleText = styled(Typography)(({ theme }) => ({
  display: 'block',
  fontSize: theme.typography.h3.fontSize,
  fontWeight: 600,
  color: theme.palette.darkNavy,
}));

const ModalDescription = styled(Typography)(({ theme }) => ({
  display: 'block',
  marginTop: theme.spacing(1.5),
  marginLeft: 'auto',
  marginRight: 'auto',
  maxWidth: 360,
  lineHeight: 1.5,
}));

const RetainerNameInCopy = styled('span')(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const CommentFieldStack = styled(Stack)(({ theme }) => ({
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
}));

const CloseRetainerDialogActions = styled(StyledDialogActions)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'stretch',
  paddingTop: theme.spacing(1),
  gap: theme.spacing(1),
}));

const CloseRetainerSubmitButton = styled(StyledSubmitButton)({
  width: '100%',
  maxWidth: '100%',
  borderRadius: 9999,
  fontWeight: 700,
});

export default CloseRetainerModal;
